/**
 * React hook for online/offline status and sync queue state.
 *
 * Listens to `online`/`offline` events and polls the pending mutation count.
 *
 * @module hooks/useOnlineStatus
 */

import { useState, useEffect, useCallback } from 'react';
import { getPendingMutationCount } from '../offline/db';
import { isSyncing as checkIsSyncing, onFlushComplete } from '../offline/queue';

export interface OnlineStatus {
  /** Whether the browser reports network connectivity. */
  isOnline: boolean;
  /** Number of pending mutations in the offline queue. */
  pendingCount: number;
  /** Whether the offline queue is currently being flushed. */
  isSyncing: boolean;
  /** Last time the queue was successfully flushed (null if never). */
  lastSyncedAt: Date | null;
}

const POLL_INTERVAL_MS = 5000;

export function useOnlineStatus(): OnlineStatus {
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true,
  );
  const [pendingCount, setPendingCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState<Date | null>(null);

  // Refresh pending count from IndexedDB
  const refreshCount = useCallback(async () => {
    try {
      const count = await getPendingMutationCount();
      setPendingCount(count);
      setIsSyncing(checkIsSyncing());
    } catch {
      // IndexedDB may not be available in some contexts
    }
  }, []);

  // Listen for online/offline events
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      void refreshCount();
    };
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [refreshCount]);

  // Listen for flush completion
  useEffect(() => {
    const unsubscribe = onFlushComplete(() => {
      setLastSyncedAt(new Date());
      setIsSyncing(false);
      void refreshCount();
    });
    return unsubscribe;
  }, [refreshCount]);

  // Poll pending count when there are pending items
  useEffect(() => {
    void refreshCount(); // initial load

    const interval = setInterval(() => {
      void refreshCount();
    }, POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [refreshCount]);

  return { isOnline, pendingCount, isSyncing, lastSyncedAt };
}
